import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { getCompanySlug } from '../../utils/subdomain';
import { useHostContext } from '../../hooks/useHostContext';
import { parseTrackingFromQrPayload } from '../../utils/qrTracking';
import BrandCredit from '../../components/BrandCredit';
import { PUBLIC_IN_BRANCH_TRANSIT_LABEL } from '../../utils/constants';

function publicStatusLabel(entry) {
  if (!entry) return '';
  if (entry.status === 'in_branch_transit') return PUBLIC_IN_BRANCH_TRANSIT_LABEL;
  return entry.status_label || entry.status;
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('ar-SY', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function TrackingSearch({ company, onSearch, error }) {
  const [input, setInput] = useState('');
  const brandColor = company?.brand_color || '#3E2C6E';

  function handleSubmit(e) {
    e.preventDefault();
    const parsed = parseTrackingFromQrPayload(input.trim());
    const id = parsed?.trackingId || parsed?.orderNumber || input.trim();
    if (id) onSearch(id);
  }

  return (
    <div className="ct-page" style={{ '--ct-brand': brandColor }}>
      <div className="ct-hero">
        {company?.logo_url ? (
          <img src={company.logo_url} alt={company.name} className="ct-hero__logo" />
        ) : (
          <div className="ct-hero__icon">📦</div>
        )}
        <h1 className="ct-hero__title">
          {company?.name || 'تتبع الطلب'}
        </h1>
        <p className="ct-hero__subtitle">أدخل رقم الطلب لمتابعة حالة طلبك</p>
      </div>

      <div className="ct-card ct-card--search">
        <form onSubmit={handleSubmit} className="ct-search-form">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="رقم الطلب أو التتبع"
            dir="ltr"
            className="ct-input"
            autoComplete="off"
          />
          {error && <p className="ct-error">{error}</p>}
          <button type="submit" className="ct-btn ct-btn--primary">
            تتبع الآن
          </button>
        </form>
      </div>

      <BrandCredit variant="page" />
    </div>
  );
}

function StatusTimeline({ history, brandColor }) {
  if (!history.length) {
    return <p className="ct-empty">لا يوجد سجل حالة بعد</p>;
  }

  return (
    <div className="ct-timeline">
      {[...history].reverse().map((entry, i) => {
        const color = entry.status_color || brandColor;
        const isLatest = i === 0;
        return (
          <div key={entry.id || i} className={`ct-timeline__item${isLatest ? ' ct-timeline__item--latest' : ''}`}>
            <div className="ct-timeline__rail">
              <span className="ct-timeline__dot" style={{ backgroundColor: color, boxShadow: isLatest ? `0 0 0 4px ${color}33` : 'none' }} />
              {i < history.length - 1 && <span className="ct-timeline__line" />}
            </div>
            <div className="ct-timeline__body">
              <span className="ct-status-pill" style={{ backgroundColor: color }}>
                {publicStatusLabel(entry)}
              </span>
              {entry.note && entry.status !== 'in_branch_transit' && (
                <p className="ct-timeline__note" style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ct-muted, #666)' }}>
                  {entry.note}
                </p>
              )}
              <time className="ct-timeline__time">{formatDateTime(entry.created_at)}</time>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function TrackingPage() {
  const { tracking_id } = useParams();
  const navigate = useNavigate();
  const hostCtx = useHostContext();
  const slug = hostCtx.companySlug || getCompanySlug();
  const [company, setCompany] = useState(null);
  const [order, setOrder] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(!!tracking_id);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slug) return;
    api.getCompanyBySlug(slug)
      .then((d) => { if (d?.name) setCompany(d); })
      .catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!tracking_id) {
      setLoading(false);
      setOrder(null);
      setHistory([]);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    const companyId = company?.id || hostCtx.company?.id || null;
    api.trackOrder(tracking_id, companyId)
      .then((data) => {
        if (data.error) {
          setError(data.error);
          setLoading(false);
          return;
        }
        const orderData = data.order || data;
        const logs = data.logs || data.status_history || [];
        if (!orderData?.order_number && !orderData?.tracking_id) {
          setError('الطلب غير موجودة');
          setLoading(false);
          return;
        }
        setOrder(orderData);
        setHistory(logs);
        setLoading(false);
      })
      .catch((err) => { setError(err.message || 'حدث خطأ في الاتصال'); setLoading(false); });
  }, [tracking_id, company?.id, hostCtx.company?.id]);

  const brandColor = company?.brand_color || '#3E2C6E';

  if (!tracking_id) {
    return (
      <TrackingSearch
        company={company}
        error={error}
        onSearch={(id) => navigate(`/track/${encodeURIComponent(id)}`)}
      />
    );
  }

  if (loading) {
    return (
      <div className="ct-page" style={{ '--ct-brand': brandColor }}>
        <div className="ct-card ct-card--loading">
          <p>جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <TrackingSearch
        company={company}
        error={error || 'لم يتم العثور على الطلب'}
        onSearch={(id) => navigate(`/track/${encodeURIComponent(id)}`)}
      />
    );
  }

  const statusColor = order.status_color || brandColor;
  const statusLabel = publicStatusLabel(order);

  return (
    <div className="ct-page" style={{ '--ct-brand': brandColor }}>
      <div className="ct-hero ct-hero--compact">
        {company?.logo_url ? (
          <img src={company.logo_url} alt={company.name} className="ct-hero__logo" />
        ) : (
          <div className="ct-hero__icon">📦</div>
        )}
        <h1 className="ct-hero__title">{company?.name || 'تتبع الطلب'}</h1>
        <p className="ct-hero__order" dir="ltr">{order.order_number}</p>
      </div>

      <div className="ct-card">
        <div className="ct-current-status">
          <span className="ct-current-status__label">الحالة الحالية</span>
          <span className="ct-status-pill ct-status-pill--lg" style={{ backgroundColor: statusColor }}>
            {statusLabel}
          </span>
          {order.updated_at && (
            <time className="ct-current-status__time">آخر تحديث: {formatDateTime(order.updated_at)}</time>
          )}
        </div>

        <div className="ct-section">
          <h2 className="ct-section__title">سجل الحالة</h2>
          <StatusTimeline history={history} brandColor={brandColor} />
        </div>

        <button
          type="button"
          onClick={() => navigate('/')}
          className="ct-btn ct-btn--outline"
        >
          تتبع طلب أخرى
        </button>
      </div>

      <BrandCredit variant="page" />
    </div>
  );
}
